import React, { useState, useRef, useEffect } from 'react';

import { useTranslation } from "react-i18next";

const CreatePinInput = ({ value, onChange, error, disabled, length = 4, title, description, icon }) => {
  const { t } = useTranslation("auth");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef([]);

  // Разбиваем строку на массив символов
  const getDigitsArray = () => {
    const digits = (value || '').replace(/\D/g, '').split('');
    return Array(length).fill('').map((_, index) => digits[index] || '');
  };

  const digits = getDigitsArray();


  const updateValue = (newDigits) => {
    const newValue = newDigits.join('').replace(/\D/g, '');
    onChange(newValue);
  };


  const handleChange = (index, inputValue) => {
    const digit = inputValue.replace(/\D/g, '').slice(-1); 
    
    
    const newDigits = [...digits];
    newDigits[index] = digit;
    
    updateValue(newDigits);

    // Автоматический переход к следующему полю
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    }
  };

  // Обработка клавиш
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newDigits = [...digits];
      
      if (digits[index]) {


        newDigits[index] = '';
        updateValue(newDigits);
      } else if (index > 0) {

        newDigits[index - 1] = '';
        updateValue(newDigits);
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setFocusedIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setFocusedIndex(index + 1);
    } else if (e.key === 'Delete') {
      const newDigits = [...digits];
      newDigits[index] = '';
      updateValue(newDigits);
    } else if (/^\d$/.test(e.key)) {

        e.preventDefault();
      handleChange(index, e.key);
    } else if (!/^(Tab|Shift|Control|Alt|Meta|CapsLock|Enter|Escape)$/.test(e.key)) {

        e.preventDefault();
    }
  };


  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    
    if (pastedData) {
      const newDigits = pastedData.split('').slice(0, length);
      const filledDigits = Array(length).fill('').map((_, index) => newDigits[index] || '');
      updateValue(filledDigits);
      

      const lastFilledIndex = Math.min(newDigits.length - 1, length - 1);
      const nextIndex = newDigits.length < length ? newDigits.length : length - 1;
      
      setTimeout(() => {
        inputRefs.current[nextIndex]?.focus();
        setFocusedIndex(nextIndex);
      }, 0);
    }
  };


  
  const handleFocus = (index) => {
    setFocusedIndex(index);

    setTimeout(() => {
      inputRefs.current[index]?.select();
    }, 0);
  };


  useEffect(() => {
    if (!disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [disabled]);


  const isComplete = digits.every(digit => digit !== '') && !error;

  return (
    <div className="w-full">
      {/* Заголовок */}


      {/* Поля для ввода PIN */}
      <div className="flex justify-center gap-4 mb-6">
        {Array(length).fill(null).map((_, index) => (
          <input
            key={index}
            ref={el => inputRefs.current[index] = el}
            type="password"
            inputMode="numeric"
            maxLength={length}
            value={digits[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            onPaste={handlePaste}
            disabled={disabled}
            className={`
              w-16 h-16 text-center text-2xl font-bold
              border-2 rounded-xl
              transition-all duration-200 ease-in-out
              focus:outline-none focus:ring-0
              disabled:bg-gray-50 disabled:cursor-not-allowed
              ${error 
                ? 'border-red-300 bg-red-50 text-red-900 focus:border-red-500 animate-shake' 
                : focusedIndex === index
                  ? 'border-blue-500 bg-white shadow-lg shadow-blue-500/10 scale-105'
                  : digits[index]
                    ? 'border-green-300 bg-green-50 text-green-900'
                    : 'border-gray-300 bg-white hover:border-gray-400'
              }
            `}
          />
        ))}
      </div>

      {/* Индикатор прогресса */}
      <div className="flex justify-center mb-6">
        <div className="flex gap-2">
          {Array(length).fill(null).map((_, index) => (
            <div
              key={index}
              className={`
                w-3 h-3 rounded-full transition-all duration-300
                ${digits[index] 
                  ? error 
                    ? 'bg-red-400' 
                    : 'bg-green-400'
                  : focusedIndex === index
                    ? 'bg-blue-400 scale-125'
                    : 'bg-gray-200'
                }
              `}
            />
          ))}
        </div>
      </div>

      {/* Сообщение об ошибке */}
      {error && (
        <div className="flex items-center justify-center text-red-600 mb-4 animate-fadeIn">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">{error}</span>
        </div>
      )}

      {/* Успешное завершение */}
      {isComplete && (
        <div className="flex items-center justify-center text-green-600 animate-fadeIn">
          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center mr-2">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <span className="text-sm font-medium">{t("PIN_ENTERED")}</span>
        </div>
      )}

      {/* Подсказка */}
      {!error && !isComplete && (
        <div className="text-center">
          <div className="text-sm text-gray-500 mb-1">
            {t("ENTERED")}: {digits.filter(d => d).length} {t("FOR")} {length} {t("NUMBERS")}
          </div>
          <div className="text-xs text-gray-400">
            {t("USE_NUMBERS_FOR_PIN")}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreatePinInput;